// migrated 1:1 from @nexato/nx-core-module, #688
import { Component, OnInit, forwardRef, inject, DestroyRef, input, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EditSelectable } from '../edit-select-form-control/edit-select-form-control.component';
import { LinkableService } from '../../services/entity-link/linkable/linkable.service';
import { LinkableFilter } from '../../entities/entityLink/linkableFilter';
import { EntityLinkInput } from '../../entities/entityLink/entityLinkInput';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'nx-multi-entity-link-widget-input',
  templateUrl: './multi-entity-link-widget-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiEntityLinkWidgetInputComponent),
      multi: true
    }
  ]
})
export class MultiEntityLinkWidgetInputComponent implements OnInit, ControlValueAccessor {

  linkedEntityType = input.required<string>();
  linkType = input.required<string>();
  label = input.required<string>();
  placeholder = input.required<string>();
  role = input<string | undefined>(undefined);

  public options: EditSelectable[] = [];

  selectedControl = new FormControl<string[]>([]);
  form = new FormGroup({
    selected: this.selectedControl
  });

  // Injected services
  private readonly linkableService = inject(LinkableService);
  private readonly destroyRef = inject(DestroyRef);

  private onChange: (value: EntityLinkInput[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Reload linkables when linkedEntityType changes
    effect(() => {
      const entityType = this.linkedEntityType();
      if (entityType) {
        this.loadLinkables("");
      }
    });
  }

  ngOnInit(): void {
    // Subscribe to form changes and emit EntityLinkInput array
    this.selectedControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(selectedIds => {
        const entityLinkInputs = this.convertToEntityLinkInputs(selectedIds);
        this.onChange(entityLinkInputs);
        this.onTouched();
      });
  }

  /**
   * Remote filter callback for dropdown
   */
  onFilter = (filterValue: string) => {
    this.loadLinkables(filterValue);
  };

  /**
   * Load available linkables from backend
   */
  private loadLinkables(filterText: string): void {
    const linkableFilter: LinkableFilter = {
      entityTypes: [this.linkedEntityType()],
      text: filterText
    };

    this.linkableService.getLinkables(linkableFilter)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(linkables => linkables.map(linkable => ({
          id: linkable.id,
          name: linkable.text
        }))),
        catchError(error => {
          console.error('Error loading linkables:', error);
          return of([]);
        })
      )
      .subscribe(options => {
        this.options = options;
      });
  }

  /**
   * Converts selected IDs to EntityLinkInput array with only inward fields
   */
  private convertToEntityLinkInputs(inwardEntityIds: string[]): EntityLinkInput[] {
    if (!inwardEntityIds || inwardEntityIds.length === 0) {
      return [];
    }

    return inwardEntityIds.map(inwardEntityId => {
      const entityLinkInput = new EntityLinkInput();
      entityLinkInput.inwardEntityId = inwardEntityId;
      entityLinkInput.inwardEntityType = this.linkedEntityType();
      entityLinkInput.linkType = this.linkType();

      const roleValue = this.role();
      if (roleValue) {
        entityLinkInput.role = roleValue;
      }

      return entityLinkInput;
    });
  }

  // ControlValueAccessor implementation

  writeValue(value: EntityLinkInput[] | null): void {
    if (value && value.length > 0) {
      const ids = value.map(input => input.inwardEntityId);
      this.selectedControl.setValue(ids, { emitEvent: false });
    } else {
      this.selectedControl.setValue([], { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: EntityLinkInput[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectedControl.disable();
    } else {
      this.selectedControl.enable();
    }
  }
}
